import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-enterprise";
import { useAuthStore } from "../../store";
import { UserModel } from "../../types/onBoardingModel";
import { useGetAllHissaUsers } from "../../queries/user";
import QuickSearch from "../../components/QuickSearch";
import { PrimaryCTAButton } from "../../components/CTAButtonComponents";
import { sideBar } from "../../utils/AgGridConfig";
import { AuthListRender, CreatedAtRender, UpdatedAtRender } from "./Users";
import { DateUtil } from "../../utils/dateUtils";
import { CompanyFilesPopup } from "../FileExplorer/FileExplorerPopup";

const HissaUserTable: React.FC = () => {
  const user = useAuthStore();
  const gridApi = useRef<any>(null);
  const [show, setShow] = useState(false);

  const { data: hissaUserData } = useGetAllHissaUsers();
  const [listOfUser, setListOfUser] = useState<UserModel[]>([]);
  useEffect(() => {
    const userList: UserModel[] = [];
    hissaUserData?.forEach((ele: any) =>
      userList.push({
        id: ele.user.id,
        name: ele.user.fullName,
        email: ele.user.emailId,
        role: ele.user.type,
        authStrategy:
          (ele.authData && ele.authData.map((obj: any) => obj.authStrategy)) ||
          [],
        createdAt: ele.user.createdAt,
        updatedAt: ele.user.updatedAt,
      }),
    );
    setListOfUser(userList);
  }, [hissaUserData]);

  const componentsRegistery = useMemo(
    () => ({
      authStrategy: memo(AuthListRender),
      createdAt: memo(CreatedAtRender),
      updatedAt: memo(UpdatedAtRender),
    }),
    [],
  );

  const [columnDefs] = useState<ColDef<UserModel>[]>([
    {
      headerName: "Name",
      field: "name",
      filter: "agTextColumnFilter",
      sortable: true,
    },
    {
      headerName: "Email",
      field: "email",
      filter: "agTextColumnFilter",
      sortable: true,
    },
    {
      headerName: "User ID",
      field: "id",
      filter: "agTextColumnFilter",
      sortable: true,
    },
    {
      headerName: "Role",
      field: "role",
      filter: "agSetColumnFilter",
      sortable: true,
    },
    {
      headerName: "Auth Strategy",
      field: "authStrategy",
      filter: "agSetColumnFilter",
      cellRenderer: "authStrategy",
      sortable: true,
      minWidth: 350,
    },
    {
      headerName: "Created At",
      hide: true,
      field: "createdAt",
      cellRenderer: "createdAt",
      filter: "agDateColumnFilter",
      valueFormatter: (params) =>
        DateUtil.formatDate(params.value, "DD MMM YYYY"),
      filterParams: {
        comparator: (dateFromFilter: Date, cellValue: any) => {
          if (cellValue == null) return 0;

          const cellDate = new Date(cellValue);

          // Clear time for accurate comparison (optional but recommended)
          const cellDateNoTime = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          );

          const filterDateNoTime = new Date(
            dateFromFilter.getFullYear(),
            dateFromFilter.getMonth(),
            dateFromFilter.getDate(),
          );

          if (cellDateNoTime < filterDateNoTime) return -1;
          if (cellDateNoTime > filterDateNoTime) return 1;
          return 0;
        },
      },

      sortable: true,
    },
    {
      headerName: "Updated At",
      hide: true,
      field: "updatedAt",
      cellRenderer: "updatedAt",
      filter: "agDateColumnFilter",
      valueFormatter: (params) =>
        DateUtil.formatDate(params.value, "DD MMM YYYY"),
      filterParams: {
        comparator: (dateFromFilter: Date, cellValue: any) => {
          if (cellValue == null) return 0;

          const cellDate = new Date(cellValue);

          // Clear time for accurate comparison (optional but recommended)
          const cellDateNoTime = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          );

          const filterDateNoTime = new Date(
            dateFromFilter.getFullYear(),
            dateFromFilter.getMonth(),
            dateFromFilter.getDate(),
          );

          if (cellDateNoTime < filterDateNoTime) return -1;
          if (cellDateNoTime > filterDateNoTime) return 1;
          return 0;
        },
      },

      sortable: true,
    },
  ]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: true,
      minWidth: 150,
      flex: 1,
    }),
    [],
  );

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key);
        } else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  return (
    <div className="h-[calc(100vh-10vh)]">
      <div className="flex flex-row justify-between pt-4">
        <div>
          <h1 className="mt-4 text-xl text-secondary font-bold">
            Hissa Users List
          </h1>

          <p className="text-sm text-gray-500 font-medium pb-2">
            {listOfUser?.length} User
          </p>
        </div>

        <div className="justify-end flex flex-row py-2 items-center">
          <QuickSearch
            placeholder={`Search`}
            className=""
            onChange={(e) => {
              gridApi.current.api.setGridOption(
                "quickFilterText",
                e.target.value,
              );
            }}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 py-2">
        <PrimaryCTAButton
          onClick={() => {
            openToolPanel("columns");
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-4 me-1"
          >
            <path d="M9.836 3h-3.67v10h3.67V3ZM11.336 13H13.5a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 13.5 3h-2.164v10ZM2.5 3h2.166v10H2.5A1.5 1.5 0 0 1 1 11.5v-7A1.5 1.5 0 0 1 2.5 3Z" />
          </svg>
          Columns
        </PrimaryCTAButton>
        <PrimaryCTAButton
          onClick={() => {
            openToolPanel("filters");
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-4 me-1"
          >
            <path d="M14 2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2.172a2 2 0 0 0 .586 1.414l2.828 2.828A2 2 0 0 1 6 9.828v4.363a.5.5 0 0 0 .724.447l2.17-1.085A2 2 0 0 0 10 11.763V9.829a2 2 0 0 1 .586-1.414l2.828-2.828A2 2 0 0 0 14 4.172V2Z" />
          </svg>
          Filter
        </PrimaryCTAButton>
      </div>

      <div className="ag-theme-quartz pb-4 flex" style={{ height: "90%" }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={listOfUser}
          components={componentsRegistery}
          ref={gridApi}
          onGridReady={(params) => {
            gridApi.current = params;
          }}
          alwaysMultiSort
          defaultColDef={defaultColDef}
          className="w-full col"
          sideBar={sideBar}
          getRowStyle={(params) => {
            if (params.rowIndex % 2 === 0) {
              return { background: "#f8f8f8" };
            } else {
              return { background: "#ffffff" };
            }
          }}
        />
      </div>
    </div>
  );
};

export default HissaUserTable;
